<template>
  <div class="upload-file">
    <a-upload
      name="file"
      :multiple="true"
      :file-list="fileList"
      :customRequest="customRequest"
      :remove="handleRemove"
      :before-upload="beforeUpload"
      :accept="accept"
      @preview="handlePreview"
      @change="handleChange"
    >
      <div v-if="fileList.length < maxCount">
        <slot name="trigger">
          <a-button size="large">
            <a-icon type="upload" /> {{ btnText }}
          </a-button>
        </slot>
      </div>
    </a-upload>
    <a-modal :visible="previewVisible" :footer="null" @cancel="handleCancel">
      <img alt="example" style="width: 100%" :src="previewImage" />
    </a-modal>
  </div>
</template>

<script>
import { uploadFile } from '@/api/common'

export default {
  name: 'UploadFile',
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    maxCount: {
      type: Number,
      default: 99
    },
    btnText: {
      type: String,
      default: '上传附件'
    },
    accept: {
      type: String,
      default: '.jpg,.jpeg,.png,.pdf'
    },
    beforeUploadProp: {
      type: Function,
      default: null
    },
    removeFunction: {
      type: Function,
      default: null
    },
    afterUpload: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      fileList: [],
      previewVisible: false,
      previewImage: '',
    }
  },
  watch: {
    value: {
      handler(newVal) {
        const val = newVal || [];
        
        const uploadingFiles = this.fileList.filter(f => f.status === 'uploading');
        
        const newFiles = val.map(item => {
           const id = item.id;
           const exist = this.fileList.find(f => (f.response && f.response.id === id) || f.uid === id);
           if (exist) return exist;

           const name = item.fileName || item.name || '未知文件';
           const filePath =item.id+item.fileType;
           const url = item.url || (filePath ? this.getFileUrl(filePath) : '');

           return {
             uid: id,
             name: name,
             status: 'done',
             url: url,
             response: { 
               id: id, 
               filePath: filePath,
               url: url 
             }
           };
        });

        this.fileList = [...newFiles, ...uploadingFiles];
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    getFileUrl(filePath){
      if (!filePath) return '';
      if (filePath.startsWith('http')) return filePath;
      return `${process.env.VUE_APP_FILE_BASE_URL}/static/${filePath}`;
    },
    beforeUpload(file) {
      if (this.beforeUploadProp) {
        return this.beforeUploadProp(file);
      }

      if (!this.accept || this.accept === '*') {
        return true;
      }

      const acceptedTypes = this.accept.split(',').map(type => type.trim().toLowerCase());
      const fileName = file.name.toLowerCase();
      const fileType = file.type.toLowerCase();

      const isValid = acceptedTypes.some(type => {
        if (type.startsWith('.')) {
          return fileName.endsWith(type);
        }
        if (type.includes('/')) {
          if (type.endsWith('/*')) {
            return fileType.startsWith(type.slice(0, -1));
          }
          return fileType === type;
        }
        return false;
      });

      if (!isValid) {
        this.$message.error(`不支持该文件类型，仅支持：${this.accept}`);
        return false;
      }
      return true;
    },
    customRequest(data) {
      const { file, onSuccess, onError } = data
      const formData = {
        file: file
      }
      uploadFile(formData).then(res => {
        const filePath=res.id+res.fileType
        const result = {
          id: res.id,
          name: res.fileName,
          filePath: filePath,
          url: this.getFileUrl(filePath),
          status: 'done',
        }
        
        if (this.afterUpload) {
          this.afterUpload(result).then(() => {
            onSuccess(result, file)
          }).catch(() => {
            this.fileList = this.fileList.filter(f => f.uid !== file.uid)
          })
        } else {
          this.$emit('uploadSuccess', result)
          onSuccess(result, file)
        }
      }).catch(() => {
        this.$message.error('上传失败');
        this.fileList = this.fileList.filter(f => f.uid !== file.uid)
      })
    },
    handleChange({ fileList }) {
      this.fileList = fileList;

      const valueList = fileList
        .filter(file => file.status === 'done' && file.response)
        .map(file => ({
          id: file.response.id,
          fileName: file.name,
          filePath: file.response.filePath,
          url: this.getFileUrl(file.response.filePath)
        }));
      
      this.$emit('change', valueList);
      this.$emit('input', valueList);
    },
    handleRemove(file) {
      if (this.removeFunction) {
        return this.removeFunction(file);
      }
      return true;
    },
    async handlePreview(file) {
      let url = file.url;
      if (!url && file.response && file.response.url) {
         try {
            url = file.response.url;
         } catch(e) {
           this.$message.error('获取文件详情失败');
           return;
         }
      }
      if (!url) {
        this.$message.error('无法预览：文件链接不存在');
        return;
      }

      window.open(url, '_blank');
    },
    handleCancel() {
      this.previewVisible = false;
    }
  }
}
</script>

<style scoped>
.upload-file {
  width: 100%;
}
</style>